/** @format */

import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { cn } from "../../../util/classNameConstructor";

const skeletonKeyframes = keyframes`
  0% {
    background-position: 0% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

const SkeletonItem = styled.span`
  animation: ${skeletonKeyframes} 2s cubic-bezier(0.75, 0, 0.25, 1) infinite;
  background-size: 200% 100%;
`;

const useLineHeight = (ref?: React.RefObject<HTMLDivElement>) => {
  const [lineHeight, setLineHeight] = useState(0);
  const fallbackRef = useRef<HTMLDivElement>(null);
  const containerRef = ref || fallbackRef;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (containerRef.current?.parentElement) {
      const computedStyle = window.getComputedStyle(containerRef.current);
      const lineHeightValue = Number.parseFloat(computedStyle.lineHeight);

      if (!Number.isNaN(lineHeightValue)) {
        setLineHeight(lineHeightValue);
      } else {
        // If line-height is 'normal', it's typically ~1.2 * fontSize
        const fontSize = Number.parseFloat(computedStyle.fontSize);
        setLineHeight(fontSize * 1.2);
      }
    }
  }, []);

  return [lineHeight, containerRef] as const;
};

type SkeletonProps = {
  count?: number;
  height?: number;
  width?: number;
  round?: boolean;
  square?: boolean;
  ragged?: boolean;
  className?: string;
};
const Skeleton = ({
  count = 1,
  height,
  width,
  round,
  square,
  ragged,
  className,
}: SkeletonProps) => {
  const [lineHeight, containerRef] = useLineHeight();

  // Generate natural-looking paragraph widths when ragged is true
  const raggedWidths = useMemo(() => {
    if (!ragged) return [];

    return [
      "91%",
      "100%",
      "85%",
      "97%",
      "74%",
      "94%",
      "100%",
      "83%",
      "75%",
      "45%",
    ];
  }, [ragged]);

  return (
    <div
      ref={containerRef}
      className={cn("relative w-52 flex flex-col gap-2", className)}
    >
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonItem
          key={i.toString()}
          className={cn(
            "w-full rounded-md bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200",
            round && "rounded-full",
            square && "aspect-square w-min",
          )}
          style={{
            animationDelay: `${i * 0.1}s`,
            height: height
              ? `${height}px`
              : lineHeight
                ? `${lineHeight}px`
                : "1em",
            width: width
              ? `${width}px`
              : ragged
                ? raggedWidths[i % raggedWidths.length]
                : undefined,
          }}
        />
      ))}
    </div>
  );
};

export default Skeleton;
